import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import {
  Boxes, Cpu, Bot, Radar, Puzzle, Sparkles, ArrowRight, CheckCircle2,
  ClipboardList, Users, Award, Quote
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-[#15348C]/[0.06] border border-[#15348C]/10 px-4 py-1.5 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-[#F57C20]"/>
    {children}
  </span>
);

const DOMAINS = [
  { icon: Boxes, title: "Blockchain & Web3", desc: "Smart contracts, decentralised apps and enterprise ledger use cases." },
  { icon: Cpu, title: "IoT & Embedded Systems", desc: "Connected-device prototypes spanning firmware, edge compute and telemetry." },
  { icon: Bot, title: "Robotics & Automation", desc: "Applied robotics projects covering control systems, sensors and simulation." },
  { icon: Radar, title: "Edge AI & Computer Vision", desc: "On-device inference, vision pipelines and real-time detection systems." }
];

const PROCESS = [
  { title: "Scope", desc: "We co-define a real business or R&D problem statement with your team, sized for a fixed-duration project.", icon: ClipboardList },
  { title: "Build", desc: "Learners work in small pods under an industry mentor, applying the niche skill to the live problem statement.", icon: Puzzle },
  { title: "Review & Showcase", desc: "Projects are reviewed against enterprise-grade criteria and presented to stakeholders as a portfolio deliverable.", icon: Award }
];

const FEATURED_PROJECTS = [
  { tag: "Blockchain", title: "Supply-Chain Provenance Ledger", outcome: "Deployed a permissioned-chain prototype tracking 3 supplier tiers." },
  { tag: "IoT", title: "Predictive Maintenance Sensor Kit", outcome: "Reduced simulated downtime detection lag from hours to minutes." },
  { tag: "Edge AI", title: "Real-Time Defect Detection", outcome: "Achieved 94% detection accuracy on a live production-line feed." }
];

const NicheSkillProjectsPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0A1F5C] via-[#15348C] to-[#1E47A8] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "26px 26px" }}
        />
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-[100px]"/>
        <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-[#F57C20]/20 rounded-full blur-[100px]"/>
        <div className="relative max-w-5xl mx-auto px-4 py-24 lg:py-28 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#8BD835] bg-white/5 border border-white/10 px-4 py-2 rounded-full mb-7">
              <Sparkles size={14}/> NICHE SKILL PROJECTS
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold leading-[1.1]">Rare skills, proven on real projects.</h1>
            <p className="mt-5 text-gray-300 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
              Build emerging and niche capabilities &mdash; Blockchain, IoT, Robotics, Edge AI &mdash; through mentor-guided, business-relevant capstone projects, not just theory.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                Scope a Niche Skill Project <ArrowRight size={16}/>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Domains */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>EMERGING SKILL DOMAINS</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Specialised capability, built for tomorrow&rsquo;s roadmap.</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {DOMAINS.map((d, i) => (
              <Reveal key={d.title} variant="up" delay={i * 80}>
                <div className="sg-card group bg-white border border-gray-200/80 rounded-2xl p-7 h-full shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#15348C] flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                    <d.icon size={22} className="text-white"/>
                  </div>
                  <h4 className="font-bold text-[#15348C] text-lg">{d.title}</h4>
                  <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#F8F9FA] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>HOW A PROJECT COMES TO LIFE</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">From problem statement to portfolio deliverable.</h2>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-6">
            {PROCESS.map((p, i) => (
              <Reveal key={p.title} variant="up" delay={i * 100}>
                <div className="bg-white border border-gray-200/80 rounded-2xl p-7 h-full shadow-sm relative">
                  <div className="absolute top-6 right-6 text-4xl font-bold text-[#15348C]/[0.06]">{String(i + 1).padStart(2, "0")}</div>
                  <div className="w-12 h-12 rounded-xl bg-[#6BBE32]/12 flex items-center justify-center mb-5">
                    <p.icon size={22} className="text-[#6BBE32]"/>
                  </div>
                  <h3 className="text-lg font-bold text-[#15348C]">{p.title}</h3>
                  <p className="mt-2.5 text-sm text-gray-500 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured projects showcase */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>PROJECT SHOWCASE</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">A glimpse of what learner pods have shipped.</h2>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-6">
            {FEATURED_PROJECTS.map((p, i) => (
              <Reveal key={p.title} variant="up" delay={i * 100}>
                <div className="sg-card bg-gradient-to-br from-[#0A1F5C] to-[#1E47A8] text-white rounded-2xl p-7 h-full relative overflow-hidden">
                  <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-[#6BBE32]/20 rounded-full blur-2xl"/>
                  <span className="relative inline-block text-[10px] font-bold tracking-widest text-[#8BD835] bg-white/10 px-3 py-1 rounded-full">{p.tag.toUpperCase()}</span>
                  <h4 className="relative font-bold text-lg mt-4">{p.title}</h4>
                  <p className="relative text-sm text-gray-300 mt-2.5 leading-relaxed">{p.outcome}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-white border border-gray-200/80 rounded-2xl p-8 lg:p-10 shadow-sm">
              <Quote size={30} className="text-[#F57C20] mb-4"/>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                We needed IoT capability nobody on our team had. SkillsGain&rsquo;s niche project track delivered a working predictive-maintenance prototype and a team that could own it end-to-end.
              </p>
              <div className="mt-6 pt-5 border-t border-gray-200 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#15348C] text-white flex items-center justify-center font-bold text-sm shrink-0">
                  <Users size={18}/>
                </div>
                <div>
                  <div className="font-bold text-[#15348C]">Director of Innovation</div>
                  <div className="text-sm text-gray-500">Industrial Manufacturing Enterprise</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] rounded-3xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR INNOVATION &amp; L&amp;D LEADERS</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Turn a niche skill gap into a shipped project.</h2>
                  <p className="mt-4 text-gray-300 leading-relaxed">Bring us a real problem statement &mdash; we&rsquo;ll bring the mentors, curriculum and delivery pod.</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-gray-300">
                    {["Industry mentor-led", "Fixed-duration delivery", "Portfolio-ready outcomes"].map(t => (
                      <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#8BD835]"/>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                    Talk to Our Advisor <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="border-2 border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request a Proposal</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default NicheSkillProjectsPage;
